import { Link } from 'react-router'
import { Badge } from '@carouselgg/nexus/badge'
import { LeagueContact } from '../../../components/LeagueContact'
import type { StandingRow } from '../../../api/teams'
import type { CompetitionGroup } from '../competitions'
import { Panel, PanelList } from './chrome'
import { useDashboardData } from './context'
import type { WidgetManifest } from './types'
import styles from './SeasonsWidget.module.css'

/**
 * The seasons the viewer is in, with where their team sits in each.
 *
 * Standings are the answer to "how are we doing" — the thing a player opens a
 * dashboard for. Three rows, because this is a summary and the full table is
 * one click away.
 *
 * An operator does not get this: their teams table already says what their org
 * is entered in, and repeating it under a different heading made the same fact
 * appear twice on one screen.
 */

/** Seasons before the panel stops being a summary. */
const MAX_SEASONS = 2

/** Standings rows before a card stops being a summary. */
const STANDINGS_ROWS = 3

/** Places that promote. Below them the rank number is just an index. */
const PROMOTION_PLACES = 2

const STAGE_TONE: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  playoffs: 'success',
  active: 'info',
  open: 'warning',
}

const STAGE_LABEL: Record<string, string> = {
  open: 'Registering',
  active: 'In season',
  playoffs: 'In playoffs',
}

function SeasonsWidget() {
  const { competitionGroups, standings, reloadCompetitions } = useDashboardData()

  return (
    <Panel title="Your seasons" href="/compete" hrefLabel="All seasons">
      <PanelList>
        {competitionGroups.slice(0, MAX_SEASONS).map((group) => (
          <SeasonCard
            key={group.competition.season_id}
            group={group}
            standings={standings[group.competition.season_id] ?? []}
            href={`/org/${group.competition.organization_slug}/competitions/${group.competition.slug}`}
            onContactChanged={reloadCompetitions}
          />
        ))}
      </PanelList>
    </Panel>
  )
}

export function SeasonCard({
  group,
  standings,
  href,
  onContactChanged,
}: {
  group: CompetitionGroup
  standings: StandingRow[]
  href?: string
  onContactChanged: () => void
}) {
  const c = group.competition
  const mine = new Set(group.teams.map((t) => t.team_name))
  const managed = group.teams.filter((t) => t.can_manage_contact)
  // The top of the table, plus the viewer's own row even when it is not in it —
  // a team sitting 9th still needs to see 9th.
  const top = standings.slice(0, STANDINGS_ROWS)
  const ownRow = standings.find((r) => mine.has(r.team_name))
  const rows = ownRow && !top.includes(ownRow) ? [...top, ownRow] : top

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          {href ? (
            <Link to={href} className={styles.cardTitle}>
              {c.organization_name} — {c.name}
            </Link>
          ) : (
            <span className={styles.cardTitle}>
              {c.organization_name} — {c.name}
            </span>
          )}
          <div className={styles.cardSub}>
            {[
              c.game_name,
              c.division_name && `${c.division_name} division`,
              `with ${group.teams.map((t) => t.team_name).join(', ')}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <Badge variant={STAGE_TONE[c.status] ?? 'default'} size="sm">
          {STAGE_LABEL[c.status] ?? c.status}
        </Badge>
      </div>

      {rows.length > 0 && (
        <div className={styles.standings}>
          <div className={`${styles.standingsRow} ${styles.standingsHead}`}>
            <span>#</span>
            <span>Team</span>
            <span className={styles.record}>W–L</span>
            <span className={styles.diffHead}>Diff</span>
          </div>
          {rows.map((row) => (
            <div
              key={row.team_slug}
              className={`${styles.standingsRow} ${styles.standingsBody} ${
                mine.has(row.team_name) ? styles.mine : ''
              }`}
            >
              <span
                className={`${styles.rank} ${row.rank <= PROMOTION_PLACES ? styles.rankTop : ''}`}
              >
                {row.rank}
              </span>
              <span className={styles.name}>{row.team_name}</span>
              <span className={styles.record}>
                {row.wins}–{row.losses}
              </span>
              <span className={`${styles.diff} ${row.point_diff < 0 ? styles.diffDown : ''}`}>
                {row.point_diff > 0 ? '+' : ''}
                {row.point_diff}
              </span>
            </div>
          ))}
        </div>
      )}

      {managed.map((t) => (
        <LeagueContact key={t.registration_id} competition={t} onChanged={onContactChanged} showTeam={group.teams.length > 1} />
      ))}
    </div>
  )
}

export const seasonsWidget: WidgetManifest = {
  id: 'seasons',
  title: () => 'Your seasons',
  // Club and university admins too: it's where they change who a league
  // reaches about one of their teams for an event.
  roles: ['player', 'club owner', 'university admin'],
  span: 8,
  order: 40,
  // Wearing an operator hat as well, the teams table above already carries what
  // the org is entered in; this would be the same fact under a second heading —
  // unless there's an event contact here for them to manage, which it doesn't.
  relevance: (ctx) => {
    if (ctx.counts.competitions === 0) return 'hide'
    const operatorView = ctx.multiPersona || !ctx.personas.includes('player')
    if (operatorView) return ctx.counts.contactsToManage > 0 ? 'full' : 'hide'
    return 'full'
  },
  Component: SeasonsWidget,
}
